import { useMemo, useState } from "react";
import { fmtInt } from "../usagePeriods";

/**
 * Tarjeta "Servidor": RAM del backend (lo que cobra el PaaS) y cuánto pesa cada socket
 * de WhatsApp. El costo por sesión es una estimación: Node solo mide el proceso entero,
 * así que se reparte lo que creció sobre la base entre los sockets abiertos.
 */
function Tile({ label, value, hint, tone, index }) {
  return (
    <div className={`sa-tile ${tone ? `tone-${tone}` : ''}`} style={{ '--i': index }}>
      <span className="sa-tile-label">{label}</span>
      <span className="sa-tile-value">{value}</span>
      {hint && <span className="sa-tile-hint">{hint}</span>}
    </div>
  );
}

const fmtUptime = (sec) => {
  if (sec == null) return "—";
  const h = Math.floor(sec / 3600);
  if (h >= 24) return `${Math.floor(h / 24)} d ${h % 24} h`;
  if (h >= 1) return `${h} h ${Math.floor((sec % 3600) / 60)} min`;
  return `${Math.max(1, Math.floor(sec / 60))} min`;
};

const fmtTime = (ms) => new Date(ms).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });

const W = 600;
const H = 120;
const PAD = { top: 10, right: 8, bottom: 18, left: 8 };

/** RSS en el tiempo, una sola serie. Hover: línea guía + tooltip con hora, MB y sockets. */
function RssChart({ history }) {
  const [hover, setHover] = useState(null);

  const geo = useMemo(() => {
    if (history.length < 2) return null;
    const t0 = history[0].at;
    const t1 = history[history.length - 1].at;
    const values = history.map((s) => s.rssMb);
    const min = Math.min(...values);
    const max = Math.max(...values);
    // Margen para que una serie plana no quede pegada al borde.
    const lo = Math.max(0, min - Math.max(10, (max - min) * 0.15));
    const hi = max + Math.max(10, (max - min) * 0.15);
    const x = (at) => PAD.left + ((at - t0) / Math.max(1, t1 - t0)) * (W - PAD.left - PAD.right);
    const y = (v) => PAD.top + (1 - (v - lo) / (hi - lo)) * (H - PAD.top - PAD.bottom);
    const points = history.map((s) => ({ ...s, px: x(s.at), py: y(s.rssMb) }));
    return { points, min, max, t0, t1, baseline: H - PAD.bottom };
  }, [history]);

  if (!geo) {
    return <p className="sa-muted sa-health-empty">La gráfica aparece tras la primera muestra (cada 5 min).</p>;
  }

  const line = geo.points.map((p, i) => `${i ? "L" : "M"}${p.px.toFixed(1)},${p.py.toFixed(1)}`).join(" ");
  const area = `${line} L${geo.points[geo.points.length - 1].px.toFixed(1)},${geo.baseline} L${geo.points[0].px.toFixed(1)},${geo.baseline} Z`;

  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const vx = ((e.clientX - rect.left) / rect.width) * W;
    let best = geo.points[0];
    for (const p of geo.points) if (Math.abs(p.px - vx) < Math.abs(best.px - vx)) best = p;
    setHover(best);
  };

  return (
    <figure className="sa-health-chart">
      <figcaption className="sa-health-caption">
        <span>RAM del backend · {fmtTime(geo.t0)} – {fmtTime(geo.t1)}</span>
        <span>mín {fmtInt(geo.min)} MB · máx {fmtInt(geo.max)} MB</span>
      </figcaption>
      <div className="sa-health-plot">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          role="img"
          aria-label={`RAM entre ${fmtInt(geo.min)} y ${fmtInt(geo.max)} MB en las últimas horas`}
          onPointerMove={onMove}
          onPointerLeave={() => setHover(null)}
        >
          <line x1={PAD.left} x2={W - PAD.right} y1={geo.baseline} y2={geo.baseline} className="sa-health-axis" />
          <path d={area} className="sa-health-area" />
          <path d={line} className="sa-health-line" vectorEffect="non-scaling-stroke" />
          {hover && (
            <line x1={hover.px} x2={hover.px} y1={PAD.top} y2={geo.baseline} className="sa-health-guide" vectorEffect="non-scaling-stroke" />
          )}
        </svg>
        {hover && (
          <>
            <span
              className="sa-health-dot"
              style={{ left: `${(hover.px / W) * 100}%`, top: `${(hover.py / H) * 100}%` }}
            />
            <div
              className="sa-health-tip"
              style={{ left: `${(hover.px / W) * 100}%`, transform: `translateX(${hover.px > W * 0.7 ? "-100%" : hover.px < W * 0.3 ? "0" : "-50%"})` }}
            >
              <b>{fmtInt(hover.rssMb)} MB</b>
              <span>{fmtTime(hover.at)} · {hover.sockets} socket{hover.sockets === 1 ? "" : "s"}</span>
              <span>heap {fmtInt(hover.heapUsedMb)} MB</span>
            </div>
          </>
        )}
      </div>
    </figure>
  );
}

export default function ServerHealth({ health }) {
  if (!health) return null;
  const { current, baselineRssMb, perSocketMb, uptimeSec, history } = health;

  return (
    <section className="sa-section sa-kpi-group">
      <h2 className="sa-eyebrow">Servidor</h2>
      <div className="sa-tiles">
        <Tile index={0} label="RAM total" value={`${fmtInt(current.rssMb)} MB`} hint={`base ${fmtInt(baselineRssMb)} MB · arriba hace ${fmtUptime(uptimeSec)}`} tone="brand" />
        <Tile index={1} label="Heap JS" value={`${fmtInt(current.heapUsedMb)} MB`} hint={`de ${fmtInt(current.heapTotalMb)} MB reservados · nativa ${fmtInt(current.externalMb)} MB`} />
        <Tile index={2} label="Sockets WhatsApp" value={fmtInt(current.sockets)} hint="abiertos, incluye plataforma" />
        <Tile index={3} label="MB por sesión" value={perSocketMb == null ? "—" : `~${fmtInt(perSocketMb)} MB`} hint="estimado: (RAM − base) ÷ sockets" />
      </div>
      <RssChart history={history || []} />
    </section>
  );
}
